import React, { useContext, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components/macro";
import { Search } from "react-feather";
import { KeyboardContext } from "../../../utilities/virtual-keyboard";
import { SET_VIRTUAL_KEYBOARD } from "../../../store/actions/sessionActions";
import { getTouchScreen } from "../../../store/sagas/selectors";
import { TOGGLE_TIMEOUT_ACTIVE, TOGGLE_TIMEOUT_DISABLED } from "../../../store/actions/timeoutActions";
import RemoveFilterIcon from "../../../icons/clearIconWhite.png";

const MenuSearchContainer = styled.div<ContainerProps>`
    flex: 2 0 0px;
    height: 20px;
    border-radius: 4px;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    padding: 6px;
    pointer-events: auto;
    margin-right: ${props => (props.isMobile ? "0px" : "10px")};
    position: relative;
`;
const MenuSearchInput = styled.input<InputProps>`
    width: 100%;
    border: none;
    ${props => (props.isMobile ? "font-size: 16px;" : "")}
    &:hover,
    &:focus {
        outline: none;
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                    padding-top:3px;
                `}
`;

const ClearInput = styled.div`
    background: ${props => props.theme.buttonNext.backgroundColor};
    color: white;
    border-radius: 50px;
    width: fit-content;
    float: right;
    padding: 0px 1px;
    font-size: 12px;
    margin: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 14px;
    position: absolute;
    right: 2px;

    @media (hover) {
        &:hover {
            cursor: pointer;
        }
    }

    ${({ theme }) =>
        theme.bp.xxxl`
                font-size: 1em;
            `}
`;

const Overflow = styled.div`
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 35%);
    width: 35px;
    height: 100%;
    position: absolute;
    right: 0;
`;

const ClearInputIcon = styled.img`
    color: ${props => props.theme.textColorSecondary};
    height: 12px;
`;

interface InputProps {
    isMobile: boolean;
}

interface ContainerProps {
    isMobile?: boolean;
}

export const MenuSearch = ({
    searchValue,
    isMobile,
    isOpen,
    onSearchChange,
    onFocus,
}: {
    searchValue: string;
    isMobile: boolean;
    isOpen: boolean;
    onSearchChange: (value: string) => void;
    onFocus: (value: boolean) => void;
}) => {
    const dispatch = useDispatch();
    const VirtualKeyboard = useContext(KeyboardContext);
    const touchScreen = useSelector(getTouchScreen);
    const searchRef = useRef<any>(null);

    useEffect(() => {
        const handleMinifySearch = (e: any) => {
            if (!searchRef.current.contains(e.target)) {
                onFocus(false);
            } else {
                onFocus(true);
            }
        };

        if (isOpen) {
            document.addEventListener("click", handleMinifySearch, true);
        }
        return () => {
            if (isOpen) {
                document.removeEventListener("click", handleMinifySearch, true);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const clearSearch = () => {
        onSearchChange("");
    };

    const onSearchFocus = (value: boolean) => {
        if (!touchScreen) return;

        if (value) {
            VirtualKeyboard.show(input => onSearchChange(input), searchValue);
            dispatch({ type: TOGGLE_TIMEOUT_DISABLED });
        } else {
            VirtualKeyboard.hide();
            dispatch({ type: TOGGLE_TIMEOUT_ACTIVE });
        }
        dispatch({ type: SET_VIRTUAL_KEYBOARD, showVirtualKeyboard: value });
    };

    const onFocusingSearch = () => {
        onFocus(true);
    };

    return (
        <>
            <MenuSearchContainer isMobile={isMobile} ref={searchRef}>
                <Search />
                <MenuSearchInput
                    isMobile={isMobile}
                    autoComplete="off"
                    value={searchValue}
                    onChange={event => onSearchChange(event.target.value)}
                    onFocus={() => onSearchFocus(true)}
                    onBlur={() => onSearchFocus(false)}
                    onClick={onFocusingSearch}
                />
                <Overflow />
                {searchValue.length > 0 ? (
                    <ClearInput onClick={() => clearSearch()}>
                        <ClearInputIcon src={RemoveFilterIcon} />
                    </ClearInput>
                ) : null}
            </MenuSearchContainer>
        </>
    );
};
